import { FC, useState, useEffect } from "react";
import { Modal, Tabs, Select, Button, Pagination, Tooltip } from "antd";
import type { TabsProps } from "antd";
import "./PointsDetailModal.scss";
import { getPointsIncomeRecordsApi, getPointsExpenditureRecordsApi } from "../../service/points";

interface PointsDetailModalProps {
  visible: boolean;
  onClose: () => void;
}

interface PointsAcquiredItem {
  id: number;
  points: number;
  pointsType: string;
  pointsSource: string;
  acquisitionTime: string;
  expirationTime: string;
  status: string;
}

interface PointsConsumedItem {
  id: number;
  pointsConsumed: number;
  functionType: string;
  taskName: string;
  videoDuration: string;
  submissionTime: string;
  transactionId: string;
  status: string;
}

export const PointsDetailModal: FC<PointsDetailModalProps> = ({
  visible,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<"acquired" | "consumed">("acquired");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [timeSort, setTimeSort] = useState<string>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(7);
  const [acquiredTotal, setAcquiredTotal] = useState(0);

  // 后端数据 - 积分获取
  const [acquiredData, setAcquiredData] = useState<PointsAcquiredItem[]>([]);

  // 后端数据 - 积分消耗
  const [consumedData, setConsumedData] = useState<PointsConsumedItem[]>([]);
  const [consumedTotal, setConsumedTotal] = useState(0);

  // 过滤和排序后的数据
  const getFilteredAndSortedData = () => {
    // 两个tab都采用后端分页，这里直接返回当前页数据
    return activeTab === "acquired" ? acquiredData : consumedData;
  };

  const filteredData = getFilteredAndSortedData();
  const total = activeTab === "acquired" ? acquiredTotal : consumedTotal;
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  const pagedData = filteredData;
  const displayStart = total === 0 ? 0 : start + 1;
  const displayEnd = total === 0 ? 0 : Math.min(end, total);

  // 切换标签页时重置页码
  useEffect(() => {
    setCurrentPage(1);
    setStatusFilter("all");
    setTimeSort("desc");
  }, [activeTab]);

  // 获取积分获取明细（后端）
  useEffect(() => {
    if (!visible || activeTab !== "acquired") return;

    const fetchAcquiredPoints = async () => {
      try {
        // 状态映射：all 不传；生效中=1；已失效=2
        let statusParam: number | undefined;
        if (statusFilter === "生效中") statusParam = 1;
        else if (statusFilter === "已失效") statusParam = 2;

        // 排序映射：1=按获取时间升序, 2=按获取时间降序
        const sortParam = timeSort === "asc" ? 1 : 2;

        const incomeParams: {
          pageNo: number;
          pageSize: number;
          sort: number;
          status?: number;
        } = {
          pageNo: currentPage,
          pageSize,
          sort: sortParam,
        };
        if (typeof statusParam === "number") {
          incomeParams.status = statusParam;
        }

        const res = await getPointsIncomeRecordsApi(incomeParams);

        if (res.code === 0 && res.data) {
          const list = res.data.list || [];
          setAcquiredTotal(res.data.total || list.length);
          setAcquiredData(
            list.map((item, index) => ({
              id: index, // 接口未返回唯一ID，这里用索引占位
              points: item.pointsIncome,
              pointsType: mapPointType(item.pointType),
              pointsSource: mapPointsSource(item.source),
              acquisitionTime: item.createTime,
              expirationTime: item.expireTime,
              status: mapIncomeStatus(item.status),
            }))
          );
        } else {
          setAcquiredData([]);
          setAcquiredTotal(0);
        }
      } catch (error) {
        console.error("获取积分获取明细失败:", error);
        setAcquiredData([]);
        setAcquiredTotal(0);
      }
    };

    fetchAcquiredPoints();
  }, [visible, activeTab, statusFilter, timeSort, currentPage, pageSize]);

  const mapPointType = (type: number): string => {
    switch (type) {
      case 1:
        return "充值";
      case 2:
        return "赠送";
      case 3:
        return "活动";
      default:
        return "其他";
    }
  };

  const mapPointsSource = (source: number): string => {
    switch (source) {
      case 1:
        return "线上";
      case 2:
        return "线下";
      case 3:
        return "系统";
      default:
        return "其他";
    }
  };

  const mapIncomeStatus = (status: number): string => {
    switch (status) {
      case 1:
        return "生效中";
      case 2:
        return "已失效";
      default:
        return "未知";
    }
  };

  // 获取积分消耗明细（后端）
  useEffect(() => {
    if (!visible || activeTab !== "consumed") return;

    const fetchConsumedPoints = async () => {
      try {
        // 状态映射：all 不传；具体数值由后端定义（这里示例：已扣除=1；已返还=2）
        let statusParam: number | undefined;
        if (statusFilter === "已扣除") statusParam = 1;
        else if (statusFilter === "已返还") statusParam = 2;

        // 排序映射：1=按获取时间升序, 2=按获取时间降序
        const sortParam = timeSort === "asc" ? 1 : 2;

        const expenditureParams: {
          pageNo: number;
          pageSize: number;
          sort: number;
          status?: number;
        } = {
          pageNo: currentPage,
          pageSize,
          sort: sortParam,
        };
        if (typeof statusParam === "number") {
          expenditureParams.status = statusParam;
        }

        const res = await getPointsExpenditureRecordsApi(expenditureParams);

        if (res.code === 0 && res.data) {
          const list = res.data.list || [];
          setConsumedTotal(res.data.total || list.length);
          setConsumedData(
            list.map((item, index) => ({
              id: index,
              pointsConsumed: item.expenditurePoints,
              functionType: mapFunctionType(item.functionType),
              taskName: item.taskName || "-",
              videoDuration: formatDuration(item.duration),
              submissionTime: item.createTime,
              transactionId: item.serialId,
              status: mapExpenditureStatus(item.status),
            }))
          );
        } else {
          setConsumedData([]);
          setConsumedTotal(0);
        }
      } catch (error) {
        console.error("获取积分消耗明细失败:", error);
        setConsumedData([]);
        setConsumedTotal(0);
      }
    };

    fetchConsumedPoints();
  }, [visible, activeTab, statusFilter, timeSort, currentPage, pageSize]);

  const mapFunctionType = (type: number): string => {
    switch (type) {
      case 1:
        return "字幕擦除";
      case 2:
        return "视频生成";
      case 3:
        return "字幕翻译";
      default:
        return "其他功能";
    }
  };

  const mapExpenditureStatus = (status: number): string => {
    switch (status) {
      case 1:
        return "已扣除";
      case 2:
        return "已返还";
      default:
        return "未知";
    }
  };

  const formatDuration = (seconds: number | null): string => {
    if (!seconds || seconds <= 0) return "-";
    const total = Math.floor(seconds);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  // 处理分页变化
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // 处理充值按钮点击
  const handleRecharge = () => {
    // TODO: 实现充值逻辑
    console.log("充值");
  };

  // 获取状态筛选选项
  const getStatusOptions = () => {
    if (activeTab === "acquired") {
      return [
        { value: "all", label: "全部" },
        { value: "生效中", label: "生效中" },
        { value: "已失效", label: "已失效" },
      ];
    } else {
      return [
        { value: "all", label: "全部" },
        { value: "已扣除", label: "已扣除" },
        { value: "已返还", label: "已返还" },
      ];
    }
  };

  // 渲染筛选和操作栏
  const renderFilterActionsBar = () => (
    <div className="filter-actions-bar">
      <div className="filter-controls">
        <Select
          value={statusFilter}
          onChange={(value) => {
            setStatusFilter(value);
            setCurrentPage(1);
          }}
          className="filter-select"
          placeholder="按状态筛选"
          getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
        >
          {getStatusOptions().map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
        <Select
          value={timeSort}
          onChange={(value) => {
            setTimeSort(value);
            setCurrentPage(1);
          }}
          className="filter-select"
          placeholder="按时间排序"
          getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
        >
          <Select.Option value="desc">降序</Select.Option>
          <Select.Option value="asc">升序</Select.Option>
        </Select>
      </div>
      <Button type="primary" onClick={handleRecharge}>
        充值
      </Button>
    </div>
  );

  // 渲染积分获取内容
  const renderAcquiredContent = () => (
    <div className="points-tab-content">
      {/* 表格 */}
      <div className="points-table-container">
        <table className="points-table">
          <thead>
            <tr>
              <th>积分数</th>
              <th>积分类型</th>
              <th>积分来源</th>
              <th>获取时间</th>
              <th>到期时间</th>
              <th>状态</th>
            </tr>
          </thead>
          <tbody>
            {pagedData.length > 0 ? (
              (pagedData as PointsAcquiredItem[]).map((item) => (
                <tr key={item.id}>
                  <td className="points-amount">+{item.points}</td>
                  <td>{item.pointsType}</td>
                  <td>{item.pointsSource}</td>
                  <td>{item.acquisitionTime}</td>
                  <td>{item.expirationTime}</td>
                  <td>
                    <span
                      className={`status-badge ${
                        item.status === "生效中" ? "active" : "expired"
                      }`}
                    >
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="no-data">
                  暂无数据
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // 渲染积分消耗内容
  const renderConsumedContent = () => (
    <div className="points-tab-content">
      {/* 表格 */}
      <div className="points-table-container">
        <table className="points-table">
          <thead>
            <tr>
              <th>消耗积分数</th>
              <th>功能类型</th>
              <th>任务名称</th>
              <th>视频时长</th>
              <th>提交时间</th>
              <th>流水ID</th>
              <th>状态</th>
            </tr>
          </thead>
          <tbody>
            {pagedData.length > 0 ? (
              (pagedData as PointsConsumedItem[]).map((item) => (
                <tr key={item.id}>
                  <td className="points-amount consumed">
                    {item.pointsConsumed}
                  </td>
                  <td>{item.functionType}</td>
                  <td className="task-name-cell">
                    <Tooltip title={item.taskName} placement="top">
                      <span className="task-name" title={item.taskName}>
                        {item.taskName}
                      </span>
                    </Tooltip>
                  </td>
                  <td>{item.videoDuration}</td>
                  <td>{item.submissionTime}</td>
                  <td>{item.transactionId}</td>
                  <td>
                    <span
                      className={`status-badge ${
                        item.status === "已扣除"
                          ? "deducted"
                          : "refunded"
                      }`}
                    >
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="no-data">
                  暂无数据
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Tabs配置
  const tabItems: TabsProps["items"] = [
    {
      key: "acquired",
      label: "积分获取",
      children: renderAcquiredContent(),
    },
    {
      key: "consumed",
      label: "积分消耗",
      children: renderConsumedContent(),
    },
  ];

  return (
    <Modal
      title="积分明细"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      className="points-detail-modal"
      closeIcon={<i className="fas fa-times"></i>}
      getContainer={false}
    >
      <div className="points-detail-content">
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as "acquired" | "consumed")}
          className="points-tabs"
          items={tabItems}
          tabBarExtraContent={renderFilterActionsBar()}
        />

        {/* 分页器和分页信息 */}
        <div className="points-pagination-container">
          <div className="pagination-info">
            <span>
              显示{displayStart}至{displayEnd}条,共{total}条
            </span>
          </div>
          <div className="points-pagination">
            {total > 0 && (
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={total}
                onChange={handlePageChange}
                showSizeChanger={false}
                showQuickJumper={false}
                simple
              />
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
